import supabase from "./supabase";
import { toast } from "react-toastify";

export interface RetakeInfo {
  text: string;
  color: string;
  canRetake: boolean;
}

export interface RetakeOptions {
  noRetake: "No retakes";
  oneRetake: "One retake";
}

/**
 * Get retake information for an assignment
 */
export function getRetakeInfo(assignment: any): RetakeInfo {
  const retakeOption = assignment.assignments?.retake_option;
  const retakesRemaining = assignment.retakes_remaining;

  if (retakeOption === "oneRetake") {
    if (retakesRemaining > 0) {
      return {
        text: `${retakesRemaining} remaining`,
        color: "text-blue-600",
        canRetake: true,
      };
    } else {
      return {
        text: "No retakes left",
        color: "text-red-600",
        canRetake: false,
      };
    }
  } else {
    return {
      text: "No retakes",
      color: "text-gray-500",
      canRetake: false,
    };
  }
}

/**
 * Check if a student can retake an assignment
 */
export function canRetakeAssignment(
  retakeOption: string,
  retakesRemaining: number
): boolean {
  return retakeOption === "oneRetake" && retakesRemaining > 0;
}

/**
 * Get retake option display text
 */
export function getRetakeOptionText(retakeOption: string): string {
  const options: RetakeOptions = {
    noRetake: "No retakes",
    oneRetake: "One retake",
  };
  return options[retakeOption as keyof RetakeOptions] || "Not set";
}

/**
 * Get retake option color for teacher display
 */
export function getRetakeOptionColor(retakeOption: string): string {
  switch (retakeOption) {
    case "oneRetake":
      return "text-blue-600 font-medium";
    case "noRetake":
      return "text-gray-500 font-medium";
    default:
      return "text-gray-400 font-medium";
  }
}

/**
 * Handle retake assignment logic
 */
export async function handleRetakeAssignment(
  userId: string,
  assignmentId: string,
  onSuccess?: () => void
): Promise<boolean> {
  if (!userId || !assignmentId) {
    toast.error("Missing user or assignment information");
    return false;
  }

  try {
    // Check if student has retakes remaining
    const { data: studentAssignment, error: fetchError } = await supabase
      .from("student_assignments")
      .select("retakes_remaining")
      .eq("student_id", userId)
      .eq("assignment_id", assignmentId)
      .single();

    if (fetchError) {
      toast.error("Failed to check retake availability");
      return false;
    }

    // Get assignment details to check retake option
    const { data: assignment, error: assignmentError } = await supabase
      .from("assignments")
      .select("retake_option")
      .eq("id", assignmentId)
      .single();

    if (assignmentError) {
      toast.error("Failed to get assignment details");
      return false;
    }

    const retakeOption = assignment.retake_option;
    const canRetake = canRetakeAssignment(
      retakeOption,
      studentAssignment.retakes_remaining
    );

    if (!canRetake) {
      toast.error("No retakes remaining for this assignment");
      return false;
    }

    //preserve the previous grade_percentage for comparison
    const updateData: any = {
      status: "in_progress",
      submitted_at: null,
      grade: null,
    };

    // Add retakes_remaining update for oneRetake option
    if (retakeOption === "oneRetake") {
      updateData.retakes_remaining = studentAssignment.retakes_remaining - 1;
    }

    // Update the assignment
    const { error: updateError } = await supabase
      .from("student_assignments")
      .update(updateData)
      .eq("student_id", userId)
      .eq("assignment_id", assignmentId);

    if (updateError) {
      toast.error("Failed to reset assignment");
      return false;
    }

    toast.success("Assignment reset for retake");

    if (onSuccess) {
      onSuccess();
    }

    return true;
  } catch (error) {
    console.error("Retake error:", error);
    toast.error("Failed to start retake");
    return false;
  }
}

/**
 * Validate retake settings when creating assignments
 */
export function validateRetakeSettings(retakeOption: string): boolean {
  const validOptions = ["noRetake", "oneRetake"];
  return validOptions.includes(retakeOption);
}

/**
 * Get initial retakes remaining count based on retake option
 */
export function getInitialRetakesRemaining(
  retakeOption: string
): number | null {
  switch (retakeOption) {
    case "oneRetake":
      return 1;
    case "noRetake":
      return 0;
    default:
      return 0;
  }
}

/**
 * Check if a user can access an assessment page
 * This function validates that the user has permission to take/retake the assessment
 * Teachers have unrestricted access to all assessments
 */
export async function canAccessAssessment(
  userId: string,
  assessmentId: string,
  assessmentType: string,
  assignmentId: string,
  userRole?: string
): Promise<{ canAccess: boolean; message?: string; redirectTo?: string }> {
  if (!userId || !assessmentId || !assignmentId) {
    return {
      canAccess: false,
      message: "Missing user or assessment information",
      redirectTo: "/dashboard",
    };
  }

  // Teachers have unrestricted access to all assessments
  if (userRole === "teacher") {
    return { canAccess: true };
  }

  try {
    // First, find the assignment for this assessment
    const { data: assignment, error: assignmentError } = await supabase
      .from("assignments")
      .select("id, retake_option")
      .eq("assessment_id", assessmentId)
      .eq("type", assessmentType)
      .eq("id", assignmentId)
      .maybeSingle();

    if (assignmentError || !assignment) {
      return {
        canAccess: false,
        message: "Assignment not found",
        redirectTo: "/dashboard",
      };
    }

    // Get the student's assignment record
    const { data: studentAssignment, error: studentError } = await supabase
      .from("student_assignments")
      .select("status, retakes_remaining")
      .eq("student_id", userId)
      .eq("assignment_id", assignment.id)
      .maybeSingle();

    if (studentError || !studentAssignment) {
      return {
        canAccess: false,
        message: "You are not assigned to this assessment",
        redirectTo: "/dashboard",
      };
    }

    // If assignment is not started or in progress, allow access
    if (
      studentAssignment.status === "not_started" ||
      studentAssignment.status === "in_progress"
    ) {
      return { canAccess: true };
    }

    // If assignment is completed, check retake permissions
    if (studentAssignment.status === "completed") {
      const canRetake = canRetakeAssignment(
        assignment.retake_option,
        studentAssignment.retakes_remaining
      );

      if (!canRetake) {
        return {
          canAccess: false,
          message: "You have no retakes remaining for this assessment",
          redirectTo: "/dashboard",
        };
      }

      return { canAccess: true };
    }

    // For any other status, deny access
    return {
      canAccess: false,
      message: "You cannot access this assessment at this time",
      redirectTo: "/dashboard",
    };
  } catch (error) {
    console.error("Error checking assessment access:", error);
    return {
      canAccess: false,
      message: "An error occurred while checking access permissions",
      redirectTo: "/dashboard",
    };
  }
}

export async function updateRetakeOption(
  assignmentId: string,
  newRetakeOption: string
) {
  if (!assignmentId || !newRetakeOption) return;

  try {
    // Update the assignment retake option
    const { error: assignmentError } = await supabase
      .from("assignments")
      .update({ retake_option: newRetakeOption })
      .eq("id", assignmentId);

    if (assignmentError) {
      console.error(
        "Error updating assignment retake option:",
        assignmentError
      );
      toast.error("Failed to update retake option");
      return false;
    }

    // Calculate the initial retakes remaining based on the new retake option
    const initialRetakesRemaining = getInitialRetakesRemaining(newRetakeOption);

    // Update all student assignments for this assignment
    const { error: studentAssignmentError } = await supabase
      .from("student_assignments")
      .update({ retakes_remaining: initialRetakesRemaining })
      .eq("assignment_id", assignmentId);

    if (studentAssignmentError) {
      console.error(
        "Error updating student assignments retake option:",
        studentAssignmentError
      );
      toast.error("Failed to update student assignment retake options");
      return false;
    }

    toast.success("Retake option updated successfully");
    return true;
  } catch (error) {
    console.error("Error updating retake option:", error);
    toast.error("Failed to update retake option");
    return false;
  }
}
